import {
  Badge, BlockStack, Box, InlineGrid, Modal, Text,
} from '@shopify/polaris';
import { runExplain, statusBadge, statusTip } from '../utils/runs';
import { friendlyError } from '../utils/sync-error-message';

/**
 * Everything about ONE background run, in words an admin can act on.
 *
 * It exists because the row cannot hold it. Type, source, brand, status, count, duration, the
 * time, an error AND a note do not fit across a table without either truncating the sentence
 * that matters or wrapping every row to two lines — which is what the note was doing before
 * this dialog existed.
 *
 * IT MUST NOT REPEAT THE ROW. The first version restated the type, the system and the count
 * from the cells the reader had just clicked, which made opening it a waste of a click. What
 * it carries now is only what the row cannot: what the job actually does to the data, what
 * happens next, the whole error rather than a truncated one, and the timings and attempts
 * that have no column.
 *
 * NO IDENTIFIERS. Not the run's id, not the queue's job id, not a brand or retailer id. They
 * are the first thing a developer reaches for and the last thing an admin can use: none of
 * them appears on any other screen, so there is nothing to compare one against.
 */

const when = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
};

/** How long it took, in words rather than a raw millisecond count. */
const howLong = (ms) => {
  if (ms == null) return null;
  if (ms < 1000) return 'under a second';
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} seconds`;
  const m = Math.floor(s / 60);
  return `${m} minute${m === 1 ? '' : 's'} ${Math.round(s % 60)} seconds`;
};

/** One labelled fact. Rendered only when there is something to say — see the caller. */
function Field({ label, children }) {
  return (
    <BlockStack gap="050">
      <Text variant="bodySm" tone="subdued">{label}</Text>
      {typeof children === 'string' ? <Text variant="bodyMd">{children}</Text> : children}
    </BlockStack>
  );
}

export default function RunDetailModal({ run, onClose }) {
  if (!run) return null;

  const duration = howLong(run.durationMs);
  const queued = when(run.createdAt);
  const started = when(run.startedAt);
  const finished = when(run.completedAt);
  const explain = runExplain(run);
  const explained = friendlyError(run.error);

  // The two are the same column in the database and mean opposite things. Rendering both from
  // one variable is how a healthy run ends up under a heading that says it broke.
  const failure = run.status === 'FAILED' ? run.error : null;
  const note = run.status === 'FAILED' ? null : (run.note || run.error);

  return (
    <Modal
      open
      onClose={onClose}
      title={run.label || 'Background run'}
      secondaryActions={[{ content: 'Close', onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* The verdict first and on its own. Everything below is detail about it, and a
              status buried among eight other fields is one nobody reads first. */}
          <BlockStack gap="150">
            {/* The same badge the row showed, from the same rule — a run that reads
                "Completed with errors" in the list must not turn plain green one click in,
                which is what a second tone map in this file had it doing. */}
            <Badge tone={statusBadge(run).tone}>{statusBadge(run).text}</Badge>
            <Text variant="bodySm" tone="subdued">{statusTip(run)}</Text>
          </BlockStack>

          {/* The one thing the row cannot say, and the reason to open this at all. */}
          {explain && (
            <BlockStack gap="050">
              <Text variant="bodySm" tone="subdued">What this job does</Text>
              <Text variant="bodyMd">{explain}</Text>
            </BlockStack>
          )}

          {failure && (
            <BlockStack gap="300">
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">What went wrong</Text>
                <Text variant="bodyMd" tone="critical" breakWord>{explained.summary}</Text>
                {explained.detail && (
                  <Text variant="bodySm" breakWord>{explained.detail}</Text>
                )}
              </BlockStack>

              {/* BOTH, always — the plain wording and the message the system actually threw.
                  A screen that only shows the friendly version is one nobody can debug from,
                  which is the trade this deliberately refuses to make. Hidden when the two
                  would be identical, since nothing was translated. */}
              {explained.known && (
                <BlockStack gap="050">
                  <Text variant="bodySm" tone="subdued">Technical detail</Text>
                  <Box background="bg-surface-secondary" padding="200" borderRadius="100">
                    <Text variant="bodySm" breakWord as="pre">{explained.raw}</Text>
                  </Box>
                </BlockStack>
              )}
            </BlockStack>
          )}

          {note && (
            <BlockStack gap="050">
              <Text variant="bodySm" tone="subdued">Worth knowing</Text>
              <Text variant="bodyMd" breakWord>{note}</Text>
            </BlockStack>
          )}

          {/* Only what has no column. Type, source and the count are all on the row the
              reader just clicked; repeating them here is what made this dialog worth
              skipping. */}
          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
            <Field label="Retailer">
              {run.retailerName
                || (run.source === 'billing'
                  ? 'None — billing does not talk to a retailer'
                  : 'Every retailer this brand is connected to')}
            </Field>
            {started && <Field label="Started">{started}</Field>}
            {finished && <Field label={run.status === 'FAILED' ? 'Stopped' : 'Finished'}>{finished}</Field>}
            {queued && !started && <Field label="Queued">{queued}</Field>}
            {run.attempts != null && (
              <Field label="Attempts">
                {run.maxAttempts
                  ? `${run.attempts} so far, up to ${run.maxAttempts} allowed`
                  : String(run.attempts)}
              </Field>
            )}
            {duration && <Field label="How long it took">{duration}</Field>}
          </InlineGrid>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
